/**
 * The dialogs the main toolbar owns. Split out of `MainToolbar.tsx` by task
 * B11 purely to get that file back under the 200-line guideline — the render
 * order and every prop are unchanged, and the component emits no DOM of its
 * own, so the toolbar's layout measurements are untouched.
 */
import { TabHistoryDialog } from '../../tab-history';
import { TableDesignerDialog } from '../../workspace/table-designer';
import { DataCompareSetupDialog } from '../../workspace/data-compare';
import { SchemaCompareSetupDialog } from '../../workspace/schema-compare';
import { RestrictedFeatureCTADialog } from '../../browser-mode';
import type { UseRestrictedFeatureDialogResult } from '../../browser-mode/hooks/useRestrictedFeatureDialog';
import { ConnectionSelectorDialog, DatabaseSelectorDialog, useContextSelectionStore } from '../../toolbar-context';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import type { DatabaseEngineId } from '@/core/engines';
import type { ResolvedTableDesignerContext } from '../hooks/useToolbarExplorerActions';

type DialogToggle = { open: boolean; setOpen: (open: boolean) => void };

type MainToolbarDialogsProps = {
  tableDesigner: DialogToggle & {
    resolvedContext: ResolvedTableDesignerContext | null;
    setResolvedContext: (context: ResolvedTableDesignerContext | null) => void;
  };
  validDbSelection: { database: string; connectionId: string } | undefined;
  explorerSelectedTable: { name: string; schema: string; database: string } | undefined;
  restrictedDialog: UseRestrictedFeatureDialogResult;
  dataCompare: DialogToggle;
  schemaCompare: DialogToggle;
};

export function MainToolbarDialogs({
  tableDesigner,
  validDbSelection,
  explorerSelectedTable,
  restrictedDialog,
  dataCompare,
  schemaCompare,
}: MainToolbarDialogsProps) {
  const { activeConnections } = useConnectionStore();
  const { setShowConnectionsDialog } = useConnectionsUIStore();
  const {
    showConnectionSelector,
    showDatabaseSelector,
    targetConnectionId,
    targetConnectionName,
    targetEngine,
    suggestedConnectionId,
    suggestedDatabase,
    engineFilter,
    selectConnection,
    selectDatabase,
    cancelSelection,
  } = useContextSelectionStore();

  return (
    <>
      {/* Tab History Dialog */}
      <TabHistoryDialog />

      {/* Table Designer Dialog - uses resolved context or validDbSelection */}
      {(tableDesigner.resolvedContext?.database || validDbSelection?.database) && (
        <TableDesignerDialog
          open={tableDesigner.open}
          onOpenChange={(open) => {
            tableDesigner.setOpen(open);
            if (!open) tableDesigner.setResolvedContext(null);
          }}
          connectionId={(tableDesigner.resolvedContext || validDbSelection)!.connectionId}
          database={(tableDesigner.resolvedContext?.database || validDbSelection?.database)!}
          explorerSelectedTable={explorerSelectedTable}
        />
      )}

      {/* Table Explorer Dialog is now rendered globally in App.tsx via GlobalTableExplorerDialog */}

      {/* Connection Selector Dialog */}
      <ConnectionSelectorDialog
        open={showConnectionSelector}
        onOpenChange={(open) => {
          if (!open) cancelSelection();
        }}
        connections={activeConnections.filter((c) => c.status === 'connected' && (!engineFilter || engineFilter(c.connection?.engine)))}
        onSelect={selectConnection}
        onManageConnections={() => {
          cancelSelection();
          setShowConnectionsDialog(true);
        }}
        suggestedConnectionId={suggestedConnectionId}
      />

      {/* Database Selector Dialog */}
      {targetConnectionId && (
        <DatabaseSelectorDialog
          open={showDatabaseSelector}
          onOpenChange={(open) => {
            if (!open) cancelSelection();
          }}
          connectionId={targetConnectionId}
          connectionName={targetConnectionName || 'Unknown'}
          engine={(targetEngine || 'mssql') as DatabaseEngineId}
          onSelect={selectDatabase}
          showSetAsDefault={true}
          suggestedDatabase={suggestedDatabase}
        />
      )}

      {/* Restricted Feature CTA Dialog (browser mode) */}
      <RestrictedFeatureCTADialog
        open={restrictedDialog.isOpen}
        feature={restrictedDialog.feature || 'terminal'}
        onClose={restrictedDialog.closeDialog}
      />

      {/* Data Compare Setup Dialog */}
      <DataCompareSetupDialog open={dataCompare.open} onOpenChange={dataCompare.setOpen} />

      {/* Schema Compare Setup Dialog */}
      <SchemaCompareSetupDialog open={schemaCompare.open} onOpenChange={schemaCompare.setOpen} />
    </>
  );
}
